import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { SocialTermsInput } from './SocialTermsInput'
import { SOCIAL_PLATFORM_LABELS } from './socialFormat'
import {
  channelBrandNames,
  communityNames,
  SocialSourceCheckState,
  sourceCheckLabelKey,
  sourceCheckTone,
  SOCIAL_UNIVERSE_MAX_HANDLES,
  SOCIAL_UNIVERSE_MAX_NARROW_COMMUNITIES,
  SOCIAL_UNIVERSE_PLATFORM_ORDER,
  UniverseDrafts,
  UniverseFormState,
} from './socialUniverseForm'

import {
  SOCIAL_PLATFORM_SOURCE_KINDS,
  SocialCommunityMode,
  SocialPlatform,
  SocialRefusedSource,
  SocialSourceCheck,
  SocialSourceCheckKind,
} from '@digital-twins/shared-types'
import { SocialTermNoteTone } from '@/types/components/social-tracker-dialog'

export interface SocialUniverseChannelCardsProps {
  form: Pick<UniverseFormState, 'platforms' | 'searches' | 'hashtags' | 'communities' | 'handles' | 'brands'>
  drafts: Pick<UniverseDrafts, 'searches' | 'hashtags' | 'communities' | 'handles'>
  /** Hashtags and communities the last Suggest did not keep, shown on their channel. */
  refusedSources: Array<SocialRefusedSource>
  /** A hashtag's or community's check running, or why it gave no answer. */
  checkState: (
    kind: SocialSourceCheckKind,
    platform: SocialPlatform,
    value: string,
  ) => SocialSourceCheckState | null
  isInvalid: boolean
  /** A picked channel without a search or hashtag is marked invalid. */
  isSearchesInvalid: boolean
  onToggle: (platform: SocialPlatform, isChecked: boolean) => void
  onSearchDraftChange: (platform: SocialPlatform, value: string) => void
  onSearchesChange: (platform: SocialPlatform, values: Array<string>) => void
  onHashtagDraftChange: (platform: SocialPlatform, value: string) => void
  onHashtagsChange: (platform: SocialPlatform, values: Array<string>) => void
  onCommunityDraftChange: (mode: SocialCommunityMode, platform: SocialPlatform, value: string) => void
  onCommunitiesChange: (mode: SocialCommunityMode, platform: SocialPlatform, names: Array<string>) => void
  onHandleDraftChange: (platform: SocialPlatform, value: string) => void
  onHandlesChange: (platform: SocialPlatform, values: Array<string>) => void
}

/**
 * One card per channel, with its checkbox (step 3, D21b); a picked card lists
 * its typed inputs: searches, and where the channel takes them
 * (SOCIAL_PLATFORM_SOURCE_KINDS) hashtags, communities and accounts to
 * follow, then a read-only line naming the brands it also searches
 * (`channelBrandNames`).
 */
export function SocialUniverseChannelCards(props: SocialUniverseChannelCardsProps) {
  const {
    form,
    drafts,
    refusedSources,
    checkState,
    isInvalid,
    isSearchesInvalid,
    onToggle,
    onSearchDraftChange,
    onSearchesChange,
    onHashtagDraftChange,
    onHashtagsChange,
    onCommunityDraftChange,
    onCommunitiesChange,
    onHandleDraftChange,
    onHandlesChange,
  } = props

  const { t } = useTranslation('app')

  return (
    <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
      {SOCIAL_UNIVERSE_PLATFORM_ORDER.map((platform) => {
        const isSelected = form.platforms.includes(platform)
        const brandNames = channelBrandNames(form, platform)
        const kinds = SOCIAL_PLATFORM_SOURCE_KINDS[platform]
        const isEmpty = form.searches[platform].length + form.hashtags[platform].length === 0
        /** Each hashtag's and community's check label and dot tone, keyed as its chip shows it. */
        const checkNotes = (
          kind: 'hashtag' | 'community',
          entries: Array<{ value: string; check?: SocialSourceCheck }>,
        ) => {
          const keys = entries.flatMap(({ value, check }) => {
            const key = sourceCheckLabelKey(
              check,
              checkState(kind, platform, kind === 'hashtag' ? value.slice(1) : value),
            )
            return key ? [[value, key] as const] : []
          })
          return {
            notes: Object.fromEntries(keys.map(([value, key]) => [value, t(key)])),
            noteTones: Object.fromEntries(keys.map(([value, key]) => [value, sourceCheckTone(key)])),
          }
        }
        const refused = refusedSources.filter(
          (source) => source.platform === platform && source.kind !== 'account',
        )
        const field = (
          key: string,
          label: string,
          input: {
            values: Array<string>
            draft: string
            placeholder: string
            isInvalid?: boolean
            notes?: Record<string, string>
            noteTones?: Record<string, SocialTermNoteTone>
            onDraftChange: (value: string) => void
            onChange: (values: Array<string>) => void
          },
        ) => (
          <div className="space-y-1.5" data-testid={`social-universe-${key}-${platform}`}>
            <Label
              htmlFor={`social-universe-${key}-${platform}`}
              className="text-xs font-normal text-muted-foreground"
            >
              {label}
            </Label>
            <SocialTermsInput
              id={`social-universe-${key}-${platform}`}
              values={input.values}
              draft={input.draft}
              placeholder={input.placeholder}
              testId={`social-universe-${key}-${platform}-input`}
              isInvalid={input.isInvalid || undefined}
              notes={input.notes}
              noteTones={input.noteTones}
              onDraftChange={input.onDraftChange}
              onChange={input.onChange}
            />
          </div>
        )
        return (
          <div
            key={platform}
            className={cn(
              'flex min-w-0 flex-col gap-3 rounded-xl border p-4 transition-colors',
              isSelected ? 'border-primary/45 bg-primary/5' : 'border-border',
            )}
            data-testid={`social-universe-channel-card-${platform}`}
          >
            <label
              htmlFor={`social-universe-channel-${platform}`}
              className="flex cursor-pointer items-start gap-3"
            >
              <Checkbox
                id={`social-universe-channel-${platform}`}
                className="mt-0.5"
                checked={isSelected}
                aria-invalid={isInvalid || undefined}
                aria-describedby={`social-universe-channel-${platform}-info`}
                data-testid={`social-universe-channel-toggle-${platform}`}
                onCheckedChange={(checked) => onToggle(platform, checked === true)}
              />
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                  <SocialPlatformIcon platform={platform} />
                  {SOCIAL_PLATFORM_LABELS[platform]}
                </span>
                <Text
                  as="span"
                  size="xs"
                  color="muted"
                  id={`social-universe-channel-${platform}-info`}
                >
                  {t(`socialListening.topicDialog.channelInfo.${platform}`)}
                </Text>
              </span>
            </label>
            {isSelected
              ? field('searches', t('socialListening.topicDialog.searches'), {
                  values: form.searches[platform].map((search) => search.query),
                  draft: drafts.searches[platform],
                  placeholder: t('socialListening.topicDialog.searchesPlaceholder'),
                  isInvalid: isSearchesInvalid && isEmpty,
                  onDraftChange: (value) => onSearchDraftChange(platform, value),
                  onChange: (values) => onSearchesChange(platform, values),
                })
              : null}
            {isSelected && kinds.hashtag
              ? field('hashtags', t('socialListening.topicDialog.hashtags'), {
                  values: form.hashtags[platform].map((hashtag) => `#${hashtag.tag}`),
                  draft: drafts.hashtags[platform],
                  placeholder: t('socialListening.topicDialog.hashtagsPlaceholder'),
                  isInvalid: isSearchesInvalid && isEmpty,
                  ...checkNotes(
                    'hashtag',
                    form.hashtags[platform].map((hashtag) => ({
                      value: `#${hashtag.tag}`,
                      check: hashtag.check,
                    })),
                  ),
                  onDraftChange: (value) => onHashtagDraftChange(platform, value),
                  onChange: (values) => onHashtagsChange(platform, values),
                })
              : null}
            {isSelected && kinds.community
              ? field('communities-narrow', t('socialListening.topicDialog.onlyCommunity'), {
                  values: communityNames(form, platform, 'narrow'),
                  draft: drafts.communities.narrow[platform],
                  placeholder: t(`socialListening.form.handlesPlaceholder.${platform}`),
                  onDraftChange: (value) => onCommunityDraftChange('narrow', platform, value),
                  onChange: (values) =>
                    onCommunitiesChange(
                      'narrow',
                      platform,
                      values.slice(-SOCIAL_UNIVERSE_MAX_NARROW_COMMUNITIES),
                    ),
                })
              : null}
            {isSelected && kinds.community
              ? field('communities-add', t('socialListening.topicDialog.alsoCommunities'), {
                  values: communityNames(form, platform, 'add'),
                  draft: drafts.communities.add[platform],
                  placeholder: t(`socialListening.form.handlesPlaceholder.${platform}`),
                  ...checkNotes(
                    'community',
                    form.communities[platform]
                      .filter((community) => community.mode === 'add')
                      .map((community) => ({ value: community.name, check: community.check })),
                  ),
                  onDraftChange: (value) => onCommunityDraftChange('add', platform, value),
                  onChange: (values) => onCommunitiesChange('add', platform, values),
                })
              : null}
            {isSelected && kinds.account
              ? field('handles', t('socialListening.topicDialog.alsoAccounts'), {
                  values: form.handles[platform],
                  draft: drafts.handles[platform],
                  placeholder: t(`socialListening.form.handlesPlaceholder.${platform}`),
                  onDraftChange: (value) => onHandleDraftChange(platform, value),
                  onChange: (values) =>
                    onHandlesChange(platform, values.slice(-SOCIAL_UNIVERSE_MAX_HANDLES)),
                })
              : null}
            {isSelected && brandNames.length ? (
              <Text
                as="span"
                size="xs"
                color="muted"
                className="block"
                data-testid={`social-universe-channel-brands-${platform}`}
              >
                {t('socialListening.topicDialog.channelBrands', {
                  count: brandNames.length,
                  names: brandNames.join(', '),
                })}
              </Text>
            ) : null}
            {isSelected && refused.length ? (
              <ul className="space-y-0.5" data-testid={`social-universe-refused-sources-${platform}`}>
                {refused.map((source) => (
                  <li key={`${source.kind}-${source.value}`}>
                    <Text as="span" size="xs" color="muted">
                      {t(`socialListening.topicDialog.refusedSource.${source.kind}`, {
                        value: source.value,
                        brand: source.brandName ?? '',
                        platform: SOCIAL_PLATFORM_LABELS[platform],
                      })}
                    </Text>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
